import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Play, Clock, Target, Bookmark, Check, ArrowRight, Loader2 } from 'lucide-react';

import { usePreviewStore } from '@/stores/previewStore';

function fmtTime(hours?: number): string | null {
  if (!hours || hours <= 0) return null;
  const mins = Math.round(hours * 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function LessonHeader({
  batchId, batchName, moduleTitle, title, topicType,
  subtitle, complexityTime, complexitySpace,
  estimatedHours, completedTopics, totalTopics,
  isCompleted, completing, hasNext,
  onPlay, onMarkComplete, onCompleteNext,
}: {
  batchId: string;
  batchName: string;
  moduleTitle: string;
  title: string;
  topicType: string;
  subtitle?: string;
  complexityTime?: string;
  complexitySpace?: string;
  estimatedHours?: number;
  completedTopics: number;
  totalTopics: number;
  isCompleted: boolean;
  completing: boolean;
  hasNext: boolean;
  onPlay: () => void;
  onMarkComplete: () => void;
  onCompleteNext: () => void;
}) {
  const [bookmarked, setBookmarked] = useState(false);
  const previewActive = usePreviewStore((s) => s.active);
  const time = fmtTime(estimatedHours);

  return (
    <header className="border-b border-[#ececec] bg-white px-4 pb-5 pt-4 sm:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] font-medium text-[#9ca3af]" aria-label="Breadcrumb">
          <Link to="/courses" className="transition-colors hover:text-[#ff5a1f]">My Batches</Link>
          <span aria-hidden>/</span>
          <Link to="/courses/$batchId" params={{ batchId }} className="text-[#ff5a1f] transition-colors hover:opacity-80">{batchName}</Link>
          <span aria-hidden>/</span>
          <Link to="/courses/$batchId/learn" params={{ batchId }} className="transition-colors hover:text-[#ff5a1f]">Learn</Link>
          <span aria-hidden>/</span>
          <Link to="/courses/$batchId/learn" params={{ batchId }} className="max-w-[180px] truncate transition-colors hover:text-[#ff5a1f]">{moduleTitle}</Link>
          <span aria-hidden>/</span>
          <span className="max-w-[180px] truncate font-semibold text-[#111827]">{title}</span>
        </nav>

        {/* Title row + actions */}
        <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <button
              type="button"
              onClick={onPlay}
              aria-label="Play recording"
              className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-[0px_8px_20px_rgba(139,92,246,0.18)] ring-1 ring-[#f3f4f6] transition-transform hover:scale-105 sm:size-[72px]"
            >
              <Play className="size-6 translate-x-px fill-[#8b5cf6] text-[#8b5cf6] sm:size-8" />
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-[26px] font-bold leading-[1.1] tracking-tight text-[#111827] sm:text-[34px]">{title}</h1>
                <span className="inline-flex items-center rounded-full bg-[#ede9fe] px-3 py-1 text-xs font-semibold text-[#8b5cf6]">Current Topic</span>
              </div>

              {/* The one-line "why" — what this topic actually buys the student. */}
              {subtitle && (
                <p className="mt-1.5 text-[15px] text-[#6b7280]">{subtitle}</p>
              )}

              {/* The pattern lives in the Overview section beside its module,
                  not here — one place, joined to the module it belongs to. */}
              {(complexityTime || complexitySpace) && (
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {(complexityTime || complexitySpace) && (
                    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-[#e5e7eb] px-2.5 py-1 font-mono text-xs text-[#9ca3af]">
                      {complexityTime && <span>time <span className="font-semibold text-[#374151]">{complexityTime}</span></span>}
                      {complexitySpace && <span>space <span className="font-semibold text-[#374151]">{complexitySpace}</span></span>}
                    </span>
                  )}
                </div>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] font-medium text-[#9ca3af]">
                {time && (
                  <span className="inline-flex items-center gap-1.5"><Clock className="size-4" />Estimated Time: {time}</span>
                )}
                <span className="inline-flex items-center gap-1.5"><Target className="size-4" />{completedTopics} of {totalTopics} topics completed</span>
              </div>
              <button
                type="button"
                onClick={() => setBookmarked((b) => !b)}
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#9ca3af] transition-colors hover:text-[#6b7280]"
              >
                <Bookmark className={`size-4 ${bookmarked ? 'fill-[#ff5a1f] text-[#ff5a1f]' : ''}`} />
                {bookmarked ? 'Bookmarked' : 'Add Bookmark'}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={onMarkComplete}
              disabled={completing || isCompleted || previewActive}
              title={previewActive ? 'Not available in student preview' : undefined}
              className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl border px-5 text-sm font-semibold transition-colors disabled:cursor-default ${
                isCompleted
                  ? 'border-[#dcfce7] bg-[#dcfce7] text-[#16a34a]'
                  : 'border-[#e5e7eb] bg-white text-[#111827] hover:bg-[#f9fafb]'
              }`}
            >
              {isCompleted ? <><Check className="size-4" />Completed</> : 'Mark as Complete'}
            </button>
            <button
              type="button"
              onClick={onCompleteNext}
              disabled={completing || (isCompleted && !hasNext) || previewActive}
              title={previewActive ? 'Not available in student preview' : undefined}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white shadow-[0px_8px_18px_rgba(139,92,246,0.25)] transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' }}
            >
              {completing
                ? <><Loader2 className="size-4 animate-spin" />Saving…</>
                : isCompleted
                  ? <>Next Topic<ArrowRight className="size-4" /></>
                  : <>Complete &amp; Next<ArrowRight className="size-4" /></>}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
